import { ReloadOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import {
  Alert,
  Button,
  Descriptions,
  Progress,
  Segmented,
  Space,
  Table,
  Tag,
  Typography,
} from 'antd'
import { useState } from 'react'
import {
  DOMAIN_LABELS,
  DOMAINS,
  QUESTION_STATUS_LABELS,
  QUESTION_TYPE_LABELS,
  QUESTION_TYPES,
  STAGE_META,
  type QuestionStatus,
} from '@baby/shared'

import { questionApi } from '../../api/questions'
import type { CoverageCount, QuestionCoverageRes, QuestionCoverageStage } from '../../api/types'
import './question-coverage.css'

type CoverageMode = 'active' | 'content'

export default function QuestionCoverage() {
  const [mode, setMode] = useState<CoverageMode>('active')
  const query = useQuery({ queryKey: ['questions', 'coverage'], queryFn: questionApi.coverage })
  const data = query.data

  if (query.error) {
    return (
      <Alert
        type="error"
        showIcon
        message="覆盖数据加载失败"
        description={query.error.message}
        action={<Button onClick={() => void query.refetch()}>重试</Button>}
      />
    )
  }

  return (
    <section className="question-coverage">
      <div className="coverage-toolbar">
        <Segmented<CoverageMode>
          value={mode}
          onChange={setMode}
          options={[
            { label: '已上架可用', value: 'active' },
            { label: '全部内容储备', value: 'content' },
          ]}
        />
        <Button
          icon={<ReloadOutlined />}
          loading={query.isFetching}
          title="刷新覆盖数据"
          onClick={() => void query.refetch()}
        />
      </div>

      <Alert
        type={mode === 'active' ? 'warning' : 'info'}
        showIcon
        message={
          mode === 'active'
            ? '只有已上架题目会进入正式考试和专项训练'
            : '内容储备包含草稿、待复核、已复核和已上架题，不包含已归档题'
        }
      />

      <CoverageSummary data={data} loading={query.isLoading} />

      <Table<QuestionCoverageStage>
        className="coverage-stage-table"
        rowKey="stage"
        size="middle"
        loading={query.isLoading}
        dataSource={data?.stages ?? []}
        pagination={false}
        scroll={{ x: 1050 }}
        expandable={{
          expandedRowRender: (row) => <CoverageDetail row={row} data={data!} mode={mode} />,
        }}
        columns={stageColumns(data, mode)}
      />
    </section>
  )
}

function CoverageSummary({ data, loading }: { data?: QuestionCoverageRes; loading: boolean }) {
  const statuses = data?.totals.statusCounts
  return (
    <Descriptions className="coverage-summary" bordered size="small" column={5}>
      <Descriptions.Item label="题库总量">{loading ? '-' : data?.totals.all}</Descriptions.Item>
      {(['ACTIVE', 'PENDING_REVIEW', 'REVIEWED', 'DRAFT'] as QuestionStatus[]).map((status) => (
        <Descriptions.Item key={status} label={QUESTION_STATUS_LABELS[status]}>
          {loading ? '-' : statuses?.[status]}
        </Descriptions.Item>
      ))}
    </Descriptions>
  )
}

function stageColumns(data: QuestionCoverageRes | undefined, mode: CoverageMode) {
  const targets = data?.targets
  return [
    {
      title: '阶段',
      width: 150,
      render: (_: unknown, row: QuestionCoverageStage) => (
        <div>
          <strong>{STAGE_META[row.stage].subject}</strong>
          <div className="coverage-secondary">{STAGE_META[row.stage].months}</div>
        </div>
      ),
    },
    {
      title: '阶段题池',
      width: 175,
      render: (_: unknown, row: QuestionCoverageStage) => (
        <CoverageMetric count={row.pool} target={targets?.stagePool ?? 300} mode={mode} />
      ),
    },
    {
      title: '阶段专属',
      width: 175,
      render: (_: unknown, row: QuestionCoverageStage) => (
        <CoverageMetric count={row.exclusive} target={targets?.stageExclusive ?? 200} mode={mode} />
      ),
    },
    {
      title: '领域最低储备',
      width: 170,
      render: (_: unknown, row: QuestionCoverageStage) => {
        const domain = [...DOMAINS].sort(
          (left, right) => valueOf(row.domains[left], mode) - valueOf(row.domains[right], mode),
        )[0]!
        return (
          <div>
            <strong>{valueOf(row.domains[domain], mode)}</strong>
            <span> / {targets?.domain ?? 50}</span>
            <div className="coverage-secondary">{DOMAIN_LABELS[domain]}</div>
          </div>
        )
      },
    },
    {
      title: '难度 1 / 2 / 3',
      width: 180,
      render: (_: unknown, row: QuestionCoverageStage) => (
        <Space size={4} wrap>
          {([1, 2, 3] as const).map((difficulty) => {
            const value = valueOf(row.difficulties[difficulty]!, mode)
            return (
              <Tag
                key={difficulty}
                color={value >= (targets?.difficulty ?? 50) ? 'green' : 'orange'}
              >
                {difficulty}: {value}
              </Tag>
            )
          })}
        </Space>
      ),
    },
    {
      title: '题型',
      width: 190,
      render: (_: unknown, row: QuestionCoverageStage) => (
        <Space size={4} wrap>
          {QUESTION_TYPES.map((type) => (
            <Tag key={type} color={type === 'MULTIPLE' ? 'blue' : undefined}>
              {QUESTION_TYPE_LABELS[type]} {valueOf(row.types[type], mode)}
            </Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '可用状态',
      width: 105,
      fixed: 'right' as const,
      render: (_: unknown, row: QuestionCoverageStage) =>
        row.ready ? (
          <Tag color="success">已达标</Tag>
        ) : (
          <Tag color="error">{row.activeGapCount} 项缺口</Tag>
        ),
    },
  ]
}

function CoverageMetric({
  count,
  target,
  mode,
}: {
  count: CoverageCount
  target: number
  mode: CoverageMode
}) {
  const value = valueOf(count, mode)
  return (
    <div className="coverage-metric">
      <div>
        <strong>{value}</strong> / {target}
        {mode === 'active' && <span className="coverage-secondary"> · 储备 {count.content}</span>}
      </div>
      <Progress
        percent={percent(value, target)}
        showInfo={false}
        size="small"
        status={value >= target ? 'success' : 'normal'}
      />
    </div>
  )
}

function CoverageDetail({
  row,
  data,
  mode,
}: {
  row: QuestionCoverageStage
  data: QuestionCoverageRes
  mode: CoverageMode
}) {
  return (
    <div className="coverage-detail">
      <CoverageGroup title="六领域">
        {DOMAINS.map((domain) => (
          <DetailMetric
            key={domain}
            label={DOMAIN_LABELS[domain]}
            count={row.domains[domain]}
            target={data.targets.domain}
            mode={mode}
          />
        ))}
      </CoverageGroup>
      <CoverageGroup title="难度">
        {([1, 2, 3] as const).map((difficulty) => (
          <DetailMetric
            key={difficulty}
            label={`难度 ${difficulty}`}
            count={row.difficulties[difficulty]!}
            target={data.targets.difficulty}
            mode={mode}
          />
        ))}
      </CoverageGroup>
      <CoverageGroup title="题型分布">
        {QUESTION_TYPES.map((type) => (
          <div className="detail-type" key={type}>
            <span>{QUESTION_TYPE_LABELS[type]}</span>
            <strong>{valueOf(row.types[type], mode)}</strong>
            {mode === 'active' && (
              <Typography.Text type="secondary">储备 {row.types[type].content}</Typography.Text>
            )}
          </div>
        ))}
      </CoverageGroup>
    </div>
  )
}

function CoverageGroup({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="coverage-group">
      <div className="coverage-group-title">{title}</div>
      {children}
    </div>
  )
}

function DetailMetric({
  label,
  count,
  target,
  mode,
}: {
  label: string
  count: CoverageCount
  target: number
  mode: CoverageMode
}) {
  const value = valueOf(count, mode)
  return (
    <div className="detail-metric">
      <span>{label}</span>
      <strong className={value < target ? 'coverage-short' : ''}>
        {value} / {target}
      </strong>
      {mode === 'active' && (
        <Typography.Text type="secondary">储备 {count.content}</Typography.Text>
      )}
    </div>
  )
}

function valueOf(count: CoverageCount, mode: CoverageMode) {
  return count[mode]
}

function percent(value: number, target: number) {
  return Math.min(100, Math.round((value / target) * 100))
}
